<template>
  <div
    :class="['small-card', { 'small-card--disabled': disabled }]"
    @click="handleClick"
    :style="styles"
  >
    <img v-if="image" class="small-card__image" :src="image" alt="logo" />
    <div class="small-card__content">
      <p class="small-card__title">{{ title }}</p>
      <p v-if="description" class="small-card__description">
        {{ description }}
      </p>
      <small class="small-card__action">
        {{ actionText }}
      </small>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue';

const props = defineProps({
  image: String,
  title: { type: String, required: true },
  description: String,
  actionText: { type: String, default: 'Go to study' },
  bgCard: String,
  bgCardHover: String,
  colorText: String,
  colorTextSecondary: String,
  colorTextAction: String,
  hasParticipants: { type: Boolean, default: true },
});

const emits = defineEmits(['onClick']);

const disabled = computed(() => !props.hasParticipants);

const styles = reactive({
  '--bg-card': props.bgCard || '#ffffff',
  '--bg-card-hover': props.bgCardHover || '#f0f4ff',
  '--text-color': props.colorText || '#2c3e50',
  '--text-color-secondary': props.colorTextSecondary || '#556779',
  '--text-color-action': props.colorTextAction || '#0066cc',
});

function handleClick() {
  if (!disabled.value) emits('onClick');
}
</script>

<style lang="scss" scoped>
.small-card {
  display: flex;
  align-items: center;
  background: var(--bg-card);
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition:
    transform 0.3s,
    box-shadow 0.3s;
  cursor: pointer;
  overflow: hidden;

  &__image {
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    object-fit: contain;
    margin: 16px;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 16px;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-color);
    margin: 0 0 4px;
    text-transform: uppercase;
    word-break: break-word;
  }

  &__description {
    font-size: 0.875rem;
    color: var(--text-color-secondary);
    margin: 0 0 8px;
  }

  &__action {
    font-size: 0.75rem;
    color: var(--text-color-action);
    text-transform: uppercase;
    align-self: flex-end;
  }

  &:hover:not(&--disabled) {
    transform: translateY(-4px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    background: var(--bg-card-hover);
  }

  &--disabled {
    opacity: 0.5;
    filter: grayscale(60%);
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
